import '../scss/components/gallery-stack.scss';

class GalleryStack {
  constructor(root) {
    this.root = root;
    this.triggers = Array.from(root.querySelectorAll('[data-gallery-stack-trigger]'));
    this.modal = root.querySelector('[data-gallery-stack-modal]');
    this.modalImage = root.querySelector('[data-gallery-stack-modal-image]');
    this.modalCaption = root.querySelector('[data-gallery-stack-modal-caption]');
    this.modalClose = root.querySelector('[data-gallery-stack-modal-close]');
    this.modalBackdrop = root.querySelector('[data-gallery-stack-backdrop]');
    this.lastTrigger = null;
    this.isOpen = false;

    this.handleKeydown = this.handleKeydown.bind(this);

    this.bindEvents();
  }

  bindEvents() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.open(trigger);
      });
    });

    if (this.modalClose) {
      this.modalClose.addEventListener('click', () => this.close());
    }

    if (this.modalBackdrop) {
      this.modalBackdrop.addEventListener('click', () => this.close());
    }

    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) {
          this.close();
        }
      });
    }
  }

  open(trigger) {
    if (!this.modal || !this.modalImage || !this.modalCaption) {
      return;
    }

    const fullSrc = trigger.dataset.fullSrc || '';
    const fullAlt = trigger.dataset.fullAlt || '';
    const figure = trigger.closest('.trust-company-gallery-stack__figure');
    const captionElement = figure ? figure.querySelector('.trust-company-gallery-stack__caption') : null;

    this.lastTrigger = trigger;
    this.isOpen = true;

    this.modalImage.src = fullSrc;
    this.modalImage.alt = fullAlt;
    this.modalCaption.innerHTML = captionElement ? captionElement.innerHTML : '';
    this.modalCaption.hidden = this.modalCaption.textContent.trim() === '';

    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('trust-company-gallery-stack--modal-open');
    document.addEventListener('keydown', this.handleKeydown);

    window.requestAnimationFrame(() => {
      this.modalClose?.focus();
    });
  }

  close() {
    if (!this.modal || !this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('trust-company-gallery-stack--modal-open');
    document.removeEventListener('keydown', this.handleKeydown);

    if (this.modalImage) {
      this.modalImage.src = '';
      this.modalImage.alt = '';
    }

    if (this.modalCaption) {
      this.modalCaption.innerHTML = '';
      this.modalCaption.hidden = true;
    }

    if (this.lastTrigger) {
      this.lastTrigger.focus({ preventScroll: true });
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.modal) {
      return;
    }

    const focusable = Array.from(
      this.modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );

    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }
}

const instances = new WeakMap();

function initGalleryStacks(context = document) {
  const roots = context.querySelectorAll('[data-gallery-stack]');

  roots.forEach((root) => {
    if (instances.has(root)) {
      return;
    }

    root.dataset.galleryStackInitialized = 'true';
    instances.set(root, new GalleryStack(root));
  });
}

function boot() {
  initGalleryStacks(document);

  if (document.body && !document.body.dataset.galleryStackObserver) {
    document.body.dataset.galleryStackObserver = 'true';

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }

          if (node.matches?.('[data-gallery-stack]')) {
            initGalleryStacks(node.parentElement || document);
            return;
          }

          if (node.querySelector?.('[data-gallery-stack]')) {
            initGalleryStacks(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
